import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFragment, usePaginationFragment } from 'react-relay/hooks';

import { createStyles, Paper, Theme, withStyles, WithStyles } from '@material-ui/core';
import { graphql } from 'babel-plugin-relay/macro';
import { concat, head, get, includes, isEmpty, map, without } from 'lodash';

import { ContentLoaderListItemChatActivity, EmptyStateListChatActivities, InfiniteScroll } from 'components/v2';
import useReduxMutation from 'hooks/useReduxMutation';
import { actionChatActivityClear } from 'sagas/sagaWatchersChatActivity';

import { useQueryClient } from 'react-query';
import { ListChatActivities_Query$key } from './__generated__/ListChatActivities_Query.graphql';
import { ListChatActivitiesPagination_Query$key } from './__generated__/ListChatActivitiesPagination_Query.graphql';
import ListItemChatActivity from './ListItemChatActivity';
import ListItemPatientChatActivityEmpty, {
  ListItemPatientChatActivityEmptyProps,
} from './ListItemPatientChatActivityEmpty';
import { TabsChatActivitiesTab } from './TabsChatActivities';
import ToolbarChatActivities, { ToolbarChatActivitiesProps } from './ToolbarChatActivities';

const styles = ({
  palette: {
    common: { white },
  },
  spacing: { unit },
}: Theme) =>
  createStyles({
    emptyMessage: {
      borderTop: '1px solid rgba(0, 0, 0, 0.25)',
      display: 'inline-block',
      height: '80%',
      marginTop: 0,
      padding: `${unit * 3}px ${unit * 3 + 4}px`,
      width: '100%',
      '&$filtering': {
        marginTop: `${unit * 2}px`,
      },
    },
    filtering: {},
    listContent: {
      backgroundColor: white,
      display: 'inline-block',
      float: 'left',
      marginTop: 0,
      padding: 0,
      width: '100%',
      '&$filtering': {
        marginTop: `${unit * 2}px !important`,
      },
    },
    listWrapper: {
      borderTop: '1px solid rgba(0, 0, 0, 0.25)',
      backgroundColor: white,
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      overflow: 'auto',
    },
    loading: {
      borderTop: '1px solid rgba(0, 0, 0, 0.25)',
      display: 'inline-block',
      marginTop: 0,
      padding: '30%',
      textAlign: 'center',
      width: '100%',
      '&$filtering': {
        marginTop: `${unit * 2}px !important`,
      },
    },
  });

export const listChatActivitiesConnectionKey = 'ListChatActivities_chatActivityConnection';

export interface ListChatActivitiesProps extends WithStyles<typeof styles> {
  fragmentRef: ListChatActivities_Query$key;
  variant: 'regular' | 'popper';
  patients: (ListItemPatientChatActivityEmptyProps['fragmentRef'] & { _id: string })[];
  selectedTab: TabsChatActivitiesTab;
  selectedPatient: string;
  filtersCount: number;
  automaticSelectionEnabled: boolean;
  includeFilters: boolean;
  onPatientSelect: (patientId?: string) => void;
  onFiltersOpen: ToolbarChatActivitiesProps['onFiltersOpen'];
  onFiltersClear: ToolbarChatActivitiesProps['onFiltersClear'];
  onChatActivityAction?: (action: 'close' | 'unread' | 'reassign', response: any) => void;
}

/**
 * TODO Cleanup legacy code for `ListChatActivities`
 *
 * @param props
 */
const ListChatActivities: React.FC<ListChatActivitiesProps> = ({
  classes,
  fragmentRef,
  variant,
  patients,
  selectedTab,
  selectedPatient,
  filtersCount,
  automaticSelectionEnabled,
  includeFilters,
  onPatientSelect,
  onFiltersOpen,
  onFiltersClear,
  onChatActivityAction,
}) => {
  const includeAssignee = !includes([TabsChatActivitiesTab.Unassigned], selectedTab);

  const data = useFragment<ListChatActivities_Query$key>(
    graphql`
      fragment ListChatActivities_Query on Query
        @argumentDefinitions(
          filter: { type: "FilterFindManyChatActivityInput!" }
          filterCount: { type: "FilterChatActivityInput!" }
          includeAssignee: { type: "Boolean!" }
        ) {
        viewer {
          roles
        }
        ...ToolbarChatActivities_Query @arguments(filter: $filterCount)
        ...ListChatActivitiesPagination_Query @arguments(filter: $filter, includeAssignee: $includeAssignee)
      }
    `,
    fragmentRef,
  );

  const {
    data: { chatActivityConnection },
    loadNext,
    isLoadingNext,
    hasNext,
  } = usePaginationFragment<any, ListChatActivitiesPagination_Query$key>(
    graphql`
      fragment ListChatActivitiesPagination_Query on Query
        @argumentDefinitions(
          cursor: { type: "String" }
          count: { type: "Int", defaultValue: 15 }
          filter: { type: "FilterFindManyChatActivityInput!" }
          includeAssignee: { type: "Boolean!" }
        )
        @refetchable(queryName: "ListChatActivitiesPaginationQuery") {
        chatActivityConnection(first: $count, after: $cursor, sort: UPDATEDAT_DESC, filter: $filter)
          @connection(key: "ListChatActivities_chatActivityConnection", filters: ["filter"]) {
          edges {
            node {
              id
              _id
              # WORKAROUND for bulk actions
              patient {
                _id
              }
              readByUser
              oid_patient
              oid_assignee
              oid_groupAssignee
              ...ListItemChatActivity_ChatActivity @arguments(includeAssignee: $includeAssignee)
            }
          }
        }
      }
    `,
    data,
  );

  const queryClient = useQueryClient();
  const [clear] = useReduxMutation(actionChatActivityClear);
  const chatActivities = useMemo(() => map(get(chatActivityConnection, 'edges'), 'node'), [chatActivityConnection]);
  const [idsChecked, setIdsChecked] = useState<string[]>([]);
  const [allChecked, setAllChecked] = useState(false);

  const includeActions = useMemo(() => {
    const array: ToolbarChatActivitiesProps['includeActions'] = [];
    if (selectedTab !== TabsChatActivitiesTab.Closed) {
      array.push('unread');
      if (selectedTab === TabsChatActivitiesTab.MyChats || includes(data.viewer!.roles, 'admin')) {
        array.push('close');
      }
    }
    return array;
  }, [selectedTab, data.viewer]);

  useEffect(() => {
    setIdsChecked([]);
    setAllChecked(false);
  }, [selectedTab, setIdsChecked, setAllChecked]);

  // Select first patient in case there is no selectedPatient
  useEffect(() => {
    if (automaticSelectionEnabled && isEmpty(chatActivities) && !isEmpty(patients) && !selectedPatient) {
      const firstNode = head(patients);
      onPatientSelect(firstNode && firstNode._id);
    }
  }, [chatActivities, automaticSelectionEnabled, patients, selectedPatient, onPatientSelect]);
  useEffect(() => {
    if (automaticSelectionEnabled && !isEmpty(chatActivities) && !selectedPatient) {
      const firstNode = head(chatActivities);
      if (firstNode) {
        onPatientSelect(firstNode.oid_patient as string);
        // Clear unread messages
        if (!firstNode.readByUser) {
          clear({ method: 'put', url: `/chatActivities/${firstNode._id}/clear` }, firstNode);
        }
      }
    }
  }, [automaticSelectionEnabled, chatActivities, selectedPatient, onPatientSelect, clear]);

  // Reset selected patient
  useEffect(() => {
    if (automaticSelectionEnabled && selectedPatient && isEmpty(chatActivities) && isEmpty(patients)) {
      onPatientSelect();
    }
  }, [automaticSelectionEnabled, chatActivities, patients, selectedPatient, onPatientSelect]);

  // Scroll to selected item
  useEffect(() => {
    if (!selectedPatient) {
      return;
    }
    const divElement = document.getElementById(selectedPatient);
    if (divElement) {
      divElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start',
      });
    }
  }, [selectedPatient]);

  const handleChatActivitySelect = useCallback(
    (id: string, selected?: boolean) => {
      if (selected) {
        setIdsChecked((ids) => concat(ids, id));
      } else {
        setIdsChecked((ids) => without(ids, id));
        if (allChecked) {
          setAllChecked(false);
        }
      }
    },
    [setIdsChecked, allChecked, setAllChecked],
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setAllChecked(checked);
      setIdsChecked(checked ? (map(chatActivities, '_id') as string[]) : []);
    },
    [setAllChecked, setIdsChecked, chatActivities],
  );

  return (
    <>
      {!patients && (
        <Paper square>
          <ToolbarChatActivities
            fragmentRef={data}
            idsChecked={idsChecked}
            allChecked={allChecked}
            filtersCount={filtersCount}
            includeActions={includeActions}
            includeFilters={includeFilters}
            onSelectAll={handleSelectAll}
            onFiltersOpen={onFiltersOpen}
            onFiltersClear={onFiltersClear}
            onChatActivityAction={onChatActivityAction}
          />
        </Paper>
      )}
      {/* Empty state */}
      {isEmpty(chatActivities) && isEmpty(patients) && (
        <EmptyStateListChatActivities
          selectedTab={selectedTab}
          filtering={!!patients}
          hasFiltersApplied={!!filtersCount}
        />
      )}
      {/* Show patients without chatActivity initiated */}
      {isEmpty(chatActivities) && !isEmpty(patients) && (
        <div className={classes.listWrapper}>
          {map(patients, (patient) => (
            <div id={patient._id} key={patient._id}>
              <ListItemPatientChatActivityEmpty
                fragmentRef={patient}
                selected={patient._id === selectedPatient}
                button
                onClick={() => {
                  // Select patient
                  if (patient._id !== selectedPatient) {
                    onPatientSelect(patient._id);
                  }
                }}
              />
            </div>
          ))}
        </div>
      )}
      {/* Show chatActivities paginated */}
      {!isEmpty(chatActivities) && (
        <div className={classes.listWrapper}>
          <InfiniteScroll
            useWindow={false}
            hasNext={hasNext}
            isLoadingNext={isLoadingNext}
            loadNext={loadNext}
            loader={<ContentLoaderListItemChatActivity />}
          >
            {map(chatActivities, (chatActivity) => (
              <div id={chatActivity.oid_patient as string} key={chatActivity._id}>
                <ListItemChatActivity
                  fragmentRef={chatActivity}
                  includeAssignee={includeAssignee}
                  selected={chatActivity.oid_patient === selectedPatient}
                  checked={allChecked || includes(idsChecked, chatActivity._id)}
                  highlighted={!chatActivity.readByUser}
                  onChatSelect={handleChatActivitySelect}
                  includeCheckbox={includeActions.length > 0}
                  button
                  onClick={() => {
                    if (chatActivity.oid_patient !== selectedPatient) {
                      // Clear unread messages
                      if (!chatActivity.readByUser) {
                        clear(
                          {
                            method: 'put',
                            url: `/chatActivities/${chatActivity._id}/clear`,
                            callbackSuccess: () => queryClient.invalidateQueries('chatActivities/unread'),
                          },
                          chatActivity,
                        );
                      }
                      onPatientSelect(chatActivity.oid_patient as string);
                    }
                  }}
                  onChatActivityAction={onChatActivityAction}
                />
              </div>
            ))}
          </InfiniteScroll>
        </div>
      )}
    </>
  );
};

export default withStyles(styles)(ListChatActivities);
